import { getControlPlaneHost } from './config'

/**
 * Minimal Control Plane transport for the admin console (Account Control Plane
 * P5). The console calls the CP host's ConnectRPC SessionService using the
 * Connect unary protocol over `fetch` with JSON codecs — a hand-rolled caller
 * (no `@bufbuild/protobuf`/generated client) because the console needs only two
 * unary methods.
 *
 * CREDENTIAL MODEL: the browser authenticates CP calls with its CP session
 * cookie (`credentials: 'include'`, sent because the calls target the CP host
 * origin) plus the `X-Libra-Csrf` double-submit header. The refresh credential
 * IS the cookie; no refresh-token string ever lives in JS. L3 (product) calls
 * use the bearer L3 token instead — that path lives in `bearer.ts`, not here.
 */

/** The L3 service audience the console presents to the L3 server. */
export const CONTROL_PLANE_L3_AUDIENCE = 'libra-l3'

/**
 * The Control Plane's own administration audience (server default
 * `CP_ADMIN_AUDIENCE`). Superadmin grant management is gated on this
 * credential plus a step-up confirmation, never on the L3 token.
 */
export const CONTROL_PLANE_ADMINISTRATION_AUDIENCE = 'libra-control-plane-admin'

const IDENTITY_PACKAGE = 'connectrpc.identity.v1'

/**
 * A Platform Administrator has no active Membership, so its access token is
 * IDENTITY_ONLY — and the `superadmin` scope the console authorizes on rides
 * exactly that identity-only L3 token. Requesting DERIVED_ORGANIZATION would
 * fail with NO_ACTIVE_MEMBERSHIP, so the console requests IDENTITY_ONLY directly.
 */
const IDENTITY_ONLY_CONTEXT = 'IDENTITY_ONLY'

let csrfToken = ''
let csrfInFlight: Promise<string> | null = null

export function clearControlPlaneCsrfToken(): void {
  csrfToken = ''
  csrfInFlight = null
}

async function ensureCsrfToken(host: string): Promise<string> {
  if (csrfToken) return csrfToken
  if (csrfInFlight) return csrfInFlight
  csrfInFlight = fetch(`${host}/session/csrf`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  })
    .then(async res => {
      if (!res.ok) throw new Error('Control Plane CSRF bootstrap failed')
      const body = (await res.json()) as { csrfToken?: unknown }
      if (typeof body.csrfToken !== 'string' || body.csrfToken.length === 0) {
        throw new Error('Control Plane CSRF bootstrap returned no token')
      }
      csrfToken = body.csrfToken
      return csrfToken
    })
    .finally(() => {
      csrfInFlight = null
    })
  return csrfInFlight
}

export interface ControlPlaneAccessToken {
  readonly audience: string
  readonly jwt: string
}

export interface SessionEstablished {
  readonly accessTokens: ControlPlaneAccessToken[]
}

async function sessionServiceCall(method: string, body: unknown): Promise<Response> {
  const host = getControlPlaneHost()
  if (!host) throw new Error('Control Plane host is not configured')
  const csrf = await ensureCsrfToken(host)
  return fetch(`${host}/${IDENTITY_PACKAGE}.SessionService/${method}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'connect-protocol-version': '1',
      'x-libra-csrf': csrf,
    },
    body: JSON.stringify(body),
  })
}

/**
 * Rotate the CP session and mint fresh IDENTITY_ONLY access tokens for both
 * audiences in one rotation. Throws on any non-OK response (caller treats a
 * throw as "no CP session" → anonymous).
 */
export async function refreshSession(rotationRequestId: string): Promise<SessionEstablished> {
  const res = await sessionServiceCall('RefreshSession', {
    rotationRequestId,
    // Empty — the HttpOnly CP cookie is the credential; the successor rides Set-Cookie.
    refreshCredential: '',
    accessTokens: [
      { audience: CONTROL_PLANE_L3_AUDIENCE, context: IDENTITY_ONLY_CONTEXT },
      { audience: CONTROL_PLANE_ADMINISTRATION_AUDIENCE, context: IDENTITY_ONLY_CONTEXT },
    ],
  })
  if (!res.ok) {
    // A stale CSRF is a common cause; drop it so the next attempt re-bootstraps.
    clearControlPlaneCsrfToken()
    throw new Error(`Control Plane RefreshSession failed (${res.status})`)
  }
  const json = (await res.json()) as { accessTokens?: unknown }
  const raw = Array.isArray(json.accessTokens) ? json.accessTokens : []
  const accessTokens = raw.filter((t): t is ControlPlaneAccessToken => {
    if (typeof t !== 'object' || t === null) return false
    if (!('audience' in t) || !('jwt' in t)) return false
    return typeof t.audience === 'string' && typeof t.jwt === 'string'
  })
  return { accessTokens }
}

/** Revoke the current refresh family (logout). Best-effort; idempotent by nature. */
export async function deleteSession(): Promise<void> {
  if (!getControlPlaneHost()) return
  try {
    await sessionServiceCall('DeleteSession', {})
  } catch {
    // Logout is defensive — a failed revoke must not block local sign-out.
  }
}

/**
 * Bearer-authenticated unary call to a Control Plane service other than
 * SessionService (Connect JSON protocol). The cookie still rides along for the
 * CSRF bootstrap; authorization is the administration-audience bearer.
 */
export async function controlPlaneCall(
  service: string,
  method: string,
  body: unknown,
  bearer: string,
): Promise<Response> {
  const host = getControlPlaneHost()
  if (!host) throw new Error('Control Plane host is not configured')
  const csrf = await ensureCsrfToken(host)
  return fetch(`${host}/${IDENTITY_PACKAGE}.${service}/${method}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'connect-protocol-version': '1',
      'x-libra-csrf': csrf,
      authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  })
}
